import { useState, useMemo, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Home } from '@lmc/ts-common'
import {
  PageHeader,
  Button,
  Spinner,
  FilterContainer,
  RangePicker
} from '@lmc/ui-kit'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import { FilePdfOutlined, EditOutlined } from '@ant-design/icons'
import { NAMESPACE } from 'locale'
import useResident from 'hooks/useResident'
import usePdfExport from 'hooks/usePdfExport'
import PageWrapper from '../componentsV2/PageWrapper'
import Legend from '../EMARResidentScreenV2/Legend'
import MedicationsTable from '../EMARResidentScreenV2/MedicationsTable'
import { StockRecordDrawer } from '../componentsV2/StockRecordDrawer/StockRecordDrawer'
import { AdministrationRecordDrawer } from '../componentsV2/AdministrationRecordDrawer'
import StockCheckModal from '../componentsV2/modalsV2/StockCheckModal'
import AddStockModal from '../componentsV2/modalsV2/AddStockModal'
import DisposalModal from '../componentsV2/modalsV2/DisposalModal'
import AdministrationModal from '../componentsV2/modalsV2/AdministrationModal'
import { StockRecord, MedicationStockReason } from '../types'
import { useMarChart } from '../hooks/useMarChart'
import { useStockRecords } from '../hooks/useStockRecords'
import { useMedicationSchedulePattern } from '../hooks/useMedicationSchedulePattern'
import { useCurrentStock } from '../hooks/useCurrentStock'
import { transformMarChartData } from '../utils/administrationUtils'
import { getMedicationIcon } from '../utils/medicationUtils'
import ExternalLink from 'components/icons/ExternalLink'
import RecordsSection from './RecordsSection'

const MedicationInformationScreenV2 = () => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const { id: residentId, medication_id: medicationId } = useParams<{
    id: string
    medication_id: string
  }>()
  const navigate = useNavigate()
  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const { resident, isLoading: isLoadingResident } = useResident({
    id: residentId
  })

  const [startDate, setStartDate] = useState(
    dayjs().subtract(14, 'days').startOf('day')
  )
  const [endDate, setEndDate] = useState(dayjs().endOf('day'))

  const { data: medicationPattern, isLoading: isLoadingMedication } =
    useMedicationSchedulePattern({
      medicationId: medicationId || '',
      enabled: !!medicationId
    })

  const { records, isLoading: isLoadingStockRecords } = useStockRecords({
    medicationId: medicationId || '',
    startDate,
    endDate,
    includeChartData: false
  })

  const { currentStock, isLoading: isLoadingCurrentStock } = useCurrentStock({
    residentMedicationId: medicationId || '',
    residentId: residentId || '',
    enabled: !!medicationId && !!residentId
  })

  const { data: marChartData, isLoading: isLoadingMarChart } = useMarChart({
    residentId: residentId || '',
    startDate,
    endDate,
    enabled: !!residentId
  })

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [administrationDrawerOpen, setAdministrationDrawerOpen] =
    useState(false)

  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null)
  const [stockCheckModalOpen, setStockCheckModalOpen] = useState(false)
  const [addStockModalOpen, setAddStockModalOpen] = useState(false)
  const [disposalModalOpen, setDisposalModalOpen] = useState(false)
  const [administrationModalOpen, setAdministrationModalOpen] = useState(false)
  const [editingRecord, setEditingRecord] = useState<StockRecord | null>(null)
  const [isEditMode, setIsEditMode] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const pdfContentRef = useRef<HTMLDivElement>(null)

  const medicationType = useMemo(() => {
    if (!medicationPattern) return 'scheduled'
    return medicationPattern.prn ? 'prn' : 'scheduled'
  }, [medicationPattern])

  const isDiscontinued = useMemo(() => {
    if (!medicationPattern) return false
    return medicationPattern.status?.toLowerCase() === 'discontinued'
  }, [medicationPattern])

  const filteredMarChartData = useMemo(() => {
    if (!marChartData || !medicationId) return null
    return {
      ...marChartData,
      medications:
        marChartData.medications?.filter(
          (med: any) => med.residentMedicationId === medicationId
        ) || []
    }
  }, [marChartData, medicationId])

  const tableData = useMemo(() => {
    return transformMarChartData(
      filteredMarChartData,
      medicationType,
      startDate,
      endDate
    )
  }, [filteredMarChartData, medicationType, startDate, endDate])

  const residentName = resident
    ? `${resident.name.first} ${resident.name.last}`
    : ''

  const handleDateChange = (dates: any) => {
    if (dates && dates[0] && dates[1]) {
      setStartDate(dates[0].startOf('day'))
      setEndDate(dates[1].endOf('day'))
    } else {
      setStartDate(dayjs().subtract(14, 'days').startOf('day'))
      setEndDate(dayjs().endOf('day'))
    }
  }

  const handleViewRecord = (recordId: string) => {
    setSelectedRecordId(recordId)
    setDrawerOpen(true)
  }

  const handleViewAdministrationRecord = (recordId: string) => {
    setSelectedRecordId(recordId)
    setAdministrationDrawerOpen(true)
  }

  const handleCloseDrawer = () => {
    setDrawerOpen(false)
    setSelectedRecordId(null)
  }

  const handleCloseAdministrationDrawer = () => {
    setAdministrationDrawerOpen(false)
    setSelectedRecordId(null)
  }

  const handleEditFromDrawer = (
    record: StockRecord,
    actionType?: MedicationStockReason
  ) => {
    if (record.type === 'administration') {
      setIsEditMode(true)
      setEditingRecord(record)
      setAdministrationModalOpen(true)
      return
    }

    if (!actionType) return

    setIsEditMode(true)
    setEditingRecord(record)

    switch (actionType) {
      case MedicationStockReason.STOCK_COUNT:
      case MedicationStockReason.RECONCILIATION:
      case MedicationStockReason.INITIAL_STOCK:
        setStockCheckModalOpen(true)
        break
      case MedicationStockReason.RESTOCK:
        setAddStockModalOpen(true)
        break
      case MedicationStockReason.DISPOSAL:
        setDisposalModalOpen(true)
        break
      default:
        break
    }
  }

  const handleStockCheckSuccess = () => {
    setStockCheckModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleStockCheckCancel = () => {
    setStockCheckModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleAddStockSuccess = () => {
    setAddStockModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleAddStockCancel = () => {
    setAddStockModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleDisposalSuccess = () => {
    setDisposalModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleDisposalCancel = () => {
    setDisposalModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleEditAdministrationCancel = () => {
    setAdministrationModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleEditAdministrationSuccess = () => {
    setAdministrationModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const expectedStock = currentStock?.currentStockLevel ?? 0

  const handleEditConfiguration = () => {
    navigate(`/residents/${residentId}/emar/${medicationId}/edit`)
  }

  const pdfTitle = `${residentName} - ${medicationPattern?.medicationName || ''} - ${startDate.format('DD-MM-YYYY')} to ${endDate.format('DD-MM-YYYY')}`
  const handleDownloadPdf = usePdfExport(
    pdfContentRef as React.RefObject<HTMLInputElement>,
    pdfTitle,
    setIsDownloading
  )

  if (
    isLoadingResident ||
    !resident ||
    isLoadingMedication ||
    isLoadingStockRecords ||
    isLoadingCurrentStock
  ) {
    return (
      <PageWrapper>
        <Spinner style={{ marginTop: '80px' }} />
      </PageWrapper>
    )
  }

  if (!medicationPattern) {
    return (
      <PageWrapper>
        <Text>{t('MEDICATION_NOT_FOUND')}</Text>
      </PageWrapper>
    )
  }

  return (
    <>
      <PageHeader
        onBack={() => navigate(-1)}
        title={
          <HeaderTitleContainer>
            {getMedicationIcon(
              isDiscontinued ? 'discontinued' : undefined,
              'large'
            )}
            <HeadingStyled level={1}>
              {medicationPattern?.medicationName}
            </HeadingStyled>
          </HeaderTitleContainer>
        }
        sticky
      >
        <HeaderButtonContainer>
          <ReadMoreLink
            href="https://support.logmycare.co.uk/en/collections/2749236-emar"
            target="_blank"
            rel="noreferrer"
          >
            {t('READ_MORE')} <ExternalLink />
          </ReadMoreLink>
          <Button
            onClick={handleEditConfiguration}
            variant="default"
            style={{ whiteSpace: 'nowrap' }}
          >
            <EditOutlined />
            {t('CONFIGURATION')}
          </Button>
          <Button
            onClick={handleDownloadPdf}
            variant="default"
            disabled={isDownloading}
            isLoading={isDownloading}
            style={{ whiteSpace: 'nowrap' }}
          >
            <FilePdfOutlined />
            {t('DOWNLOAD_PDF')}
          </Button>
        </HeaderButtonContainer>
      </PageHeader>
      <PageWrapper>
        <Container>
          <FilterContainer title="">
            <RangePicker
              defaultValue={[startDate, endDate]}
              value={[startDate, endDate]}
              onChange={handleDateChange}
              format="DD-MM-YYYY"
            />
          </FilterContainer>
          {isLoadingMarChart ? (
            <Spinner />
          ) : (
            <div ref={pdfContentRef}>
              <TableContainer>
                <TableHeader>
                  <Legend />
                </TableHeader>
                <MedicationsTable
                  startDate={startDate}
                  endDate={endDate}
                  type={medicationType}
                  residentFirstName={resident?.name?.first || ''}
                  data={tableData}
                />
              </TableContainer>
            </div>
          )}
          <RecordsSection
            records={records}
            medicationName={medicationPattern?.medicationName || ''}
            residentFirstName={resident?.name?.first || ''}
            residentFullName={
              resident ? `${resident.name.first} ${resident.name.last}` : ''
            }
            onViewRecord={handleViewRecord}
            onViewAdministrationRecord={handleViewAdministrationRecord}
          />
        </Container>
        {stockCheckModalOpen && (
          <StockCheckModal
            open={stockCheckModalOpen}
            medicationName={medicationPattern?.medicationName || ''}
            expectedStock={expectedStock}
            residentId={residentId || ''}
            residentMedicationId={medicationId || ''}
            unit={
              medicationPattern?.stockQuantityType || currentStock?.unit || ''
            }
            onCancel={handleStockCheckCancel}
            onSuccess={handleStockCheckSuccess}
            editMode={isEditMode}
            record={isEditMode ? editingRecord || undefined : undefined}
          />
        )}
        {addStockModalOpen && (
          <AddStockModal
            open={addStockModalOpen}
            medicationName={medicationPattern?.medicationName || ''}
            expectedStock={expectedStock}
            residentId={residentId || ''}
            residentMedicationId={medicationId || ''}
            unit={
              medicationPattern?.stockQuantityType || currentStock?.unit || ''
            }
            onCancel={handleAddStockCancel}
            onSuccess={handleAddStockSuccess}
            editMode={isEditMode}
            record={isEditMode ? editingRecord || undefined : undefined}
          />
        )}
        {disposalModalOpen && (
          <DisposalModal
            open={disposalModalOpen}
            medicationName={medicationPattern?.medicationName || ''}
            expectedStock={expectedStock}
            residentId={residentId || ''}
            residentMedicationId={medicationId || ''}
            unit={
              medicationPattern?.stockQuantityType || currentStock?.unit || ''
            }
            onCancel={handleDisposalCancel}
            onSuccess={handleDisposalSuccess}
            editMode={isEditMode}
            record={isEditMode ? editingRecord || undefined : undefined}
          />
        )}
        {administrationModalOpen && (
          <AdministrationModal
            open={administrationModalOpen}
            medicationName={medicationPattern?.medicationName || ''}
            record={editingRecord || undefined}
            onCancel={handleEditAdministrationCancel}
            onSuccess={handleEditAdministrationSuccess}
          />
        )}
        <StockRecordDrawer
          isVisible={drawerOpen}
          onClose={handleCloseDrawer}
          record={
            selectedRecordId
              ? records.find((r) => r.id === selectedRecordId)
              : undefined
          }
          medicationName={medicationPattern?.medicationName || ''}
          controlledDrug={medicationPattern?.controlledDrug || false}
          onEdit={handleEditFromDrawer}
        />
        <AdministrationRecordDrawer
          isVisible={administrationDrawerOpen}
          onClose={handleCloseAdministrationDrawer}
          record={
            selectedRecordId
              ? records.find((r) => r.id === selectedRecordId)
              : undefined
          }
          medicationName={medicationPattern?.medicationName || ''}
          controlledDrug={medicationPattern?.controlledDrug || false}
          onEdit={handleEditFromDrawer}
        />
      </PageWrapper>
    </>
  )
}

const HeaderTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const HeaderButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
`

const ReadMoreLink = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.color.category.clinicalCare};
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`

const TableContainer = styled.div`
  margin-top: 24px;
`

const TableHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
`

const HeadingStyled = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
  margin-right: 20px;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
`

export default MedicationInformationScreenV2
